import React, {useState, useMemo} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Modal } from 'react-bootstrap';
import { NotificationManager } from 'react-notifications';
import { showLoader, hideLoader } from '../../../store/common/slice';
import { StyledDropdownWrapper } from '../../../../admin/components/PartnersAndBuildingsDropdowns/PartnersAndBuildingsDropdowns.styled';
import { constants } from '../../../common/constants/constants';
import { useAccessToken } from '../../../common/hooks/useAccessToken';
import Close from '../../../images/newDesign/close.svg';
import warnFrame from '../../../images/newDesign/warnFrame.svg';
import { StyledTenantNotificationModal } from './debtList.styled';
import { SelectorOfRecipients } from './selectorOfRecepients/SelectorOfRecipients';
import {ArrayMap} from "../../../../utils/ArrayMap";

const SendToAllModal = ({ onSendAllModalHide, ltr, title, buildingID, apartmentTenantList, buildingAddress }) => {

    const { t } = useTranslation();
    const client = useAccessToken();
    const dispatch = useDispatch();

    const [notificationRecipientType, setNotificationRecipientType] = useState(null);
    const [notificationRecipientsList, setNotificationRecipientsList] = useState([]);
    const [requestMethods, setRequestMethods] = useState({ byEmail: false, bySMS: false, byIVR: false });

    const recipientTypes = useMemo(() => ([
        { label: t('reports.paymentRequestPopUp.labels.owner'), value: 'owner' },
        { label: t('reports.paymentRequestPopUp.labels.renter'), value: 'renter' },
    ]), [t]);

    const sendToAllButtonDisabled = useMemo(() => {
        const requestMethodSelected = Object.values(requestMethods).some(method => method);
        return !requestMethodSelected || notificationRecipientType == null || notificationRecipientsList.length < 1;
    }, [requestMethods, notificationRecipientType, notificationRecipientsList]);

    async function sendPaymentRequests() {
        if (notificationRecipientsList.length > 0) {
            dispatch(showLoader());
            const tenants = notificationRecipientsList.map(item => ({ ...item, type: notificationRecipientType }));
            const grouped = tenants.reduce((acc, next) => acc.push(next.buildingID, next), new ArrayMap());
            for (const [buildingId, tenantList] of grouped.entries()) {
                try {
                    const resp = await client.managers.operations.sendPaymentRequests(buildingId, tenantList, requestMethods);
                    if (resp?.success) {
                        NotificationManager.success(t('reports.paymentRequestPopUp.requestSuccess'), '', 5000)
                    } else {
                        NotificationManager.error(t('reports.paymentRequestPopUp.requestError'), '', 5000)
                    }
                } catch (e) {
                    console.error(e);
                    NotificationManager.error(t('reports.paymentRequestPopUp.requestError'), '', 5000)
                }
            }
            onSendAllModalHide();
            dispatch(hideLoader());
        }
    }

    function chooseRecipientsList(values) {
        setNotificationRecipientsList(values.map(value => ({
            apartmentNumber: value.label,
            buildingID: value.value.buildingID
        })));
    }

    return (
        <StyledTenantNotificationModal centered show={true} onHide={onSendAllModalHide}>
            <Modal.Body ltr={ltr ? 1 : 0}>
                <div className="notification-modal-top">
                    <h4>{t('reports.paymentRequestPopUp.sendBuildingRequestLabel', {name: title})}</h4>
                    <img src={Close} alt="close" className='close-btn' onClick={onSendAllModalHide}/>
                </div>
                <div className="notification-modal-content">
                    <div className="modal-icon">
                        <img src={warnFrame} alt="warn" />
                    </div>
                    <p className="bold">{t('reports.paymentRequestPopUp.tenantHelloText')}</p>
                    <p>{t('reports.paymentRequestPopUp.buildingPaymentAddress', { address: buildingAddress })}</p>
                    <p>{t('reports.paymentRequestPopUp.buildingHelpInfo')}</p>
                    <a href={`${constants.frontendUrl}/buildings/${buildingID}`}>{`${constants.frontendUrl}/buildings/${buildingID}`}</a>
                    <p>{t('reports.paymentRequestPopUp.importantNote')}</p>
                    <p id="author">{t('reports.paymentRequestPopUp.onBehalf')}</p>
                    <p id="help">{t('reports.paymentRequestPopUp.problemWithLink')}</p>
                </div>
                <div className="main-active-box">
                    <div className="select-recipients-box">
                        <StyledDropdownWrapper className='dropdown'>
                            <label className="label">{t('reports.paymentRequestPopUp.recipientType')}</label>
                            <SelectorOfRecipients
                                selectValues={recipientTypes}
                                callback={(values) => setNotificationRecipientType(values?.value)}
                            />
                        </StyledDropdownWrapper>
                        <div className="select-apps">
                            <span>{t('reports.paymentRequestPopUp.requestMethods')}</span>
                            <div>
                                <input
                                    id="sms"
                                    type="checkbox"
                                    checked={requestMethods.bySMS}
                                    onChange={e => setRequestMethods({...requestMethods, bySMS: e.target.checked})}
                                />
                                <label className="label" htmlFor="sms">{t('reports.paymentRequestPopUp.labels.sms')}</label>
                            </div>
                            <div>
                                <input
                                    id="email"
                                    type="checkbox"
                                    checked={requestMethods.byEmail}
                                    onChange={e => setRequestMethods({...requestMethods, byEmail: e.target.checked})}
                                />
                                <label className="label" htmlFor="email">{t('reports.paymentRequestPopUp.labels.sendEmail')}</label>
                            </div>
                            <div>
                                <input
                                    id="ivr"
                                    type="checkbox"
                                    checked={requestMethods.byIVR}
                                    onChange={e => setRequestMethods({...requestMethods, byIVR: e.target.checked})}

                                    disabled={true}
                                />
                                <label className="label" htmlFor="ivr">{t('reports.paymentRequestPopUp.labels.sendIvr')}</label>
                            </div>
                        </div>
                    </div>

                    <StyledDropdownWrapper className='dropdown'>
                        <label className="label">{t('reports.paymentRequestPopUp.recipientsSelect')}</label>
                        <SelectorOfRecipients
                            selectValues={apartmentTenantList}
                            multipleSelect={true}
                            closeMenuOnSelect={false}
                            callback={chooseRecipientsList}
                        />
                    </StyledDropdownWrapper>
                </div>
                <div className={'tenant-request-submit'}>
                    <button
                        className={'bllink-button'}
                        type="submit"
                        onClick={sendPaymentRequests}
                        disabled={sendToAllButtonDisabled}
                    >
                        {t('reports.paymentRequestPopUp.btns.send')}
                    </button>
                </div>
            </Modal.Body>
        </StyledTenantNotificationModal>
    )
}

export default SendToAllModal;
